import { useMemo } from "react";
import dayjs from "dayjs";
import { Button, Flex, theme } from "antd";
import CardSavedProposal from "../component/CardSavedProposal";
import CardStatisticPrimary from "../component/CardStatisticPrimary";
import CardStatisticSecondary from "../component/CardStatisticSecondary";
import CardTopProduct from "../component/CardTopProduct";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);

import StyledContainer from "./style";

import { getCurrencyValueWithRoundSign } from "../../../../../../../utils/helper/general";
import { accumulateProposalByProductUsage } from "../../../helper";
import { getAllManagedUsersOfAdvisors } from "../../../../../../../utils/helper/specialized";
import { getSavedProposalsAll } from "../../../../super_admin/helper";
import { getAnalyticCalculation } from "../helper";

const TemplatePageDashboard = ({
  analytics,
  managedUserManagers,
  onClickProductDetails,
  onClickViewAllProposals,
  onClickViewProposal,
  personalInfo,
  productsList,
}) => {
  const { token } = theme.useToken();
  const currentQuarter = dayjs().quarter();
  const currentYear = +dayjs().format("YYYY");
  const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
  const lastQuarterYear = currentQuarter === 1 ? currentYear - 1 : currentYear;

  const managedUsers = useMemo(
    () => getAllManagedUsersOfAdvisors(managedUserManagers),
    [managedUserManagers]
  );

  const listAllProposals = useMemo(
    () =>
      getSavedProposalsAll({
        managedUsers,
        productsList,
      }),
    [managedUserManagers, productsList]
  );

  const analyticCurrentQuarter = getAnalyticCalculation({
    managedUsers,
    managedUserManagers,
    productsList,
    searchQuarter: currentQuarter,
    searchYear: currentYear,
  });

  const analyticLastQuarter = analytics?.find(
    it => it.quarter === lastQuarter && it.year === lastQuarterYear
  );

  const ListTopProducts = () => {
    const accumulatedProducts = accumulateProposalByProductUsage({
      listAllProposals,
    });

    return (
      <Flex gap={24}>
        {accumulatedProducts.slice(0, 3).map((it, index) => (
          <CardTopProduct
            onClickProductDetails={onClickProductDetails}
            key={index}
            productData={it}
            quantitySavedProposal={listAllProposals.length}
            place={index + 1}
          />
        ))}
      </Flex>
    );
  };

  const ListRecentProposals = () =>
    listAllProposals
      ?.sort((a, b) => b.updatedAt - a.updatedAt)
      ?.slice(0, 3)
      ?.map((it, index) => (
        <CardSavedProposal
          key={index}
          onClickViewProposal={onClickViewProposal}
          proposalData={it}
        />
      ));

  return (
    <StyledContainer gap={32} token={token} vertical>
      <Flex className="container-cards" justify="center">
        <div className="container-content">
          <Flex gap={24} vertical>
            {personalInfo && (
              <h3>
                Welcome, {personalInfo?.firstName} {personalInfo?.lastName}
              </h3>
            )}

            <Flex gap={24}>
              <CardStatisticPrimary
                description={`${
                  getCurrencyValueWithRoundSign(
                    analyticLastQuarter?.totalProposedAum
                  ) ?? 0
                } Proposed this time last quarter`}
                gap={
                  ((analyticCurrentQuarter?.totalProposedAum -
                    analyticLastQuarter?.totalProposedAum) /
                    analyticLastQuarter?.totalProposedAum) *
                  100
                }
                title="Proposed business this quarter"
                value={getCurrencyValueWithRoundSign(
                  analyticCurrentQuarter.totalProposedAum
                )}
              />

              <CardStatisticPrimary
                description={`${
                  analyticLastQuarter?.quantityProposals ?? 0
                } Proposed this time last quarter`}
                gap={
                  ((analyticCurrentQuarter.quantityProposals -
                    analyticLastQuarter?.quantityProposals) /
                    analyticLastQuarter?.quantityProposals) *
                  100
                }
                title="Proposals this quarter"
                value={analyticCurrentQuarter.quantityProposals}
              />

              <CardStatisticPrimary
                description={`${
                  analyticLastQuarter?.quantityAddedCustomers ?? 0
                } added this time last quarter`}
                gap={
                  ((analyticCurrentQuarter.quantityAddedCustomers -
                    analyticLastQuarter?.quantityAddedCustomers) /
                    analyticLastQuarter?.quantityAddedCustomers) *
                  100
                }
                title="Platform Customers Added"
                value={analyticCurrentQuarter.quantityAddedCustomers}
              />
            </Flex>

            <Flex gap={24}>
              <Flex
                gap={10}
                justify="center"
                style={{
                  borderRadius: 12,
                  padding: 20,
                  background: "#EAF4F4",
                  boxShadow: "0 12px 29px 0 rgba(0, 0, 0, 0.10)",
                  textAlign: "center",
                }}
              >
                <Flex gap={6} vertical>
                  <b style={{ fontSize: 30 }}>
                    {managedUserManagers?.length ?? 0}
                  </b>
                  <span>Advisors</span>
                </Flex>
                <Flex vertical>
                  <b style={{ fontSize: 30 }}>{managedUsers?.length ?? 0}</b>
                  <span>
                    Platform
                    <br />
                    Customers
                  </span>
                </Flex>
              </Flex>

              <CardStatisticSecondary
                gap={
                  ((analyticCurrentQuarter.quantityActiveAdvisors30Days -
                    analyticCurrentQuarter?.quantityActiveAdvisors31To60Days) /
                    analyticCurrentQuarter?.quantityActiveAdvisors31To60Days) *
                  100
                }
                title={
                  <h5>
                    Active User in
                    <br /> Past 30 Days
                  </h5>
                }
                value={analyticCurrentQuarter.quantityActiveAdvisors30Days}
              />
              <CardStatisticSecondary
                gap={
                  ((analyticCurrentQuarter.quantityActiveAdvisors7Days -
                    analyticCurrentQuarter?.quantityActiveAdvisors7To14Days) /
                    analyticCurrentQuarter?.quantityActiveAdvisors7To14Days) *
                  100
                }
                title={<h5>Weekly Active Users</h5>}
                value={analyticCurrentQuarter.quantityActiveAdvisors7Days}
              />
              <CardStatisticSecondary
                gap={
                  ((analyticCurrentQuarter.quantityActiveAdvisors1Day -
                    analyticCurrentQuarter?.quantityActiveAdvisors1To2Days) /
                    analyticCurrentQuarter?.quantityActiveAdvisors1To2Days) *
                  100
                }
                title={<h5>Daily Active Users</h5>}
                value={analyticCurrentQuarter.quantityActiveAdvisors1Day}
              />
            </Flex>
          </Flex>
        </div>
      </Flex>

      <Flex className="container-proposals" justify="center">
        <div className="container-content">
          <Flex gap={32} vertical>
            <Flex gap={16} vertical>
              <h4>Top 3 Investments</h4>

              <ListTopProducts />
            </Flex>

            <Flex gap={16} vertical>
              <Flex align="center" justify="space-between">
                <h4>Recent 3 Proposals</h4>

                <Button
                  onClick={onClickViewAllProposals}
                  shape="round"
                  size="small"
                >
                  All Proposals
                </Button>
              </Flex>

              <ListRecentProposals />
            </Flex>
          </Flex>
        </div>
      </Flex>
    </StyledContainer>
  );
};

export default TemplatePageDashboard;
